import { MyActionListItem } from "interfaces/case-action";
import { useMemo } from "react";
import { styled } from "@mui/material/styles";
import WorkIcon from "@mui/icons-material/Work";

import {
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Tab,
  Tabs,
  Typography
} from "@mui/material";
import ProgressIndicatorContainer from "components/ProgressIndicatorContainer";
import { DateWithTooltip } from "components/DateWithTooltip";

export interface MyActionsListProps {
  actions: MyActionListItem[];
  loading: boolean;
  error: boolean;
  selectedListTab: number;
  selectedActionId: number | null;
  onChangeSelectedListTab: (tab: number) => void;
  onChangeSelectedActionId: (actionId: number) => void;
}

export const MyActionsList = ({
  actions,
  loading,
  error,
  selectedListTab,
  selectedActionId,
  onChangeSelectedListTab,
  onChangeSelectedActionId
}: MyActionsListProps): JSX.Element => {
  const noDataText = useMemo(() => {
    let text = "You have no actions";
    switch (selectedListTab) {
      case 1:
        text = "You have no Open or In Progress actions";
        break;
      case 2:
        text = "You have no Submitted actions in review";
        break;
      case 3:
        text = "You have no Closed actions";
        break;
      case 4:
      default:
        break;
    }

    return text;
  }, [selectedListTab]);

  const SHOW_LIST_AVATARS = false;

  return (
    <>
      <StyledTabs
        data-qa="MA-Actions-List-Tabs"
        value={selectedListTab}
        onChange={(
          _event: React.SyntheticEvent<Element, Event>,
          value: number
        ) => onChangeSelectedListTab(value)}
        variant="fullWidth"
      >
        <StyledTab label="Open / In Progress" value={1} />
        <StyledTab label="Submitted" value={2} />
        <StyledTab label="Closed" value={3} />
        <StyledTab label="All" value={4} />
      </StyledTabs>

      {!!actions.length && (
        <List
          aria-label="Your Actions"
          sx={{
            overflowY: "auto",
            py: 0,
            width: "100%"
          }}
          data-qa="MA-Actions-Card-List"
        >
          {actions?.map(action => {
            const isSelected =
              !!selectedActionId && action.id === selectedActionId;
            return (
              <ListItem
                key={action.id}
                disablePadding
                divider
                secondaryAction={
                  <Chip label={action.action_status} size="small" />
                }
                data-projectid={action.project_id ?? 0}
              >
                <ListItemButton
                  onClick={() => onChangeSelectedActionId(action.id)}
                  selected={isSelected}
                  aria-current={isSelected}
                >
                  {SHOW_LIST_AVATARS && (
                    <ListItemAvatar>
                      <Avatar>
                        <WorkIcon />
                      </Avatar>
                    </ListItemAvatar>
                  )}
                  <ListItemText
                    primary={action.action_type}
                    secondary={
                      <>
                        Action ID: {action.id}
                        <br />
                        Due date:{" "}
                        {action?.requested_completion_date ? (
                          <DateWithTooltip
                            date={action.requested_completion_date}
                            showTime={false}
                          />
                        ) : (
                          <>Not set</>
                        )}
                      </>
                    }
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      )}

      <ProgressIndicatorContainer
        loading={loading}
        error={error}
        errorMessage="An error occured whilst trying to load your actions"
      />

      {!loading && !error && !actions.length && (
        <Typography sx={{ textAlign: "center" }}>{noDataText}</Typography>
      )}
    </>
  );
};

const StyledTabs = styled(Tabs)`
  display: flex;
  justify-content: center;
  background: transparent;
`;
StyledTabs.displayName = "StyledTabs";

const StyledTab = styled(Tab)`
  color: black;
  min-width: unset;
  width: 25%;
  font-size: 0.65rem;
  text-transform: capitalize;
  word-break: break-word;
  padding: ${({ theme }) => theme.spacing(0.5)};
`;
StyledTab.displayName = "StyledTab";
